class ProductRecommendations extends HTMLElement {
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.target !== this || mutation.type !== 'attributes') continue;
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      ) continue;
      this.#loadRecommendations();
      break;
    }
  });

  #cachedRecommendations = {};
  #activeFetch = null;

  connectedCallback() {
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) throw new Error('Product ID and an ID attribute are required');
    if (recommendationsPerformed === 'true') return;

    const maxProducts = parseInt(new URL(this.dataset.url, location.href).searchParams.get('limit')) || 6;

    if (intent === 'complementary') {
      // Fetch complementary first, then fill up with related
      this.#fetchCachedRecommendations(productId, sectionId, 'complementary')
        .then((compResult) => {
          if (!compResult.success) {
            if (!Shopify.designMode) this.#handleError(new Error(`Server returned ${compResult.status}`));
            return;
          }

          const compHtml = document.createElement('div');
          compHtml.innerHTML = compResult.data || '';
          const compSection = compHtml.querySelector(`product-recommendations[id="${id}"]`);
          const compItems = compSection ? [...compSection.querySelectorAll('.resource-list__item')] : [];
          const compIds = compItems.map(el => el.querySelector('[data-product-id]')?.dataset.productId).filter(Boolean);

          const needed = maxProducts - compItems.length;

          if (needed <= 0) {
            // Complementary fills everything
            this.dataset.recommendationsPerformed = 'true';
            this.innerHTML = compSection.innerHTML;
            return;
          }

          // Fetch related to fill remaining slots
          this.#fetchCachedRecommendations(productId, sectionId, 'related')
            .then((relResult) => {
              const relHtml = document.createElement('div');
              relHtml.innerHTML = relResult.success ? (relResult.data || '') : '';
              const relSection = relHtml.querySelector(`product-recommendations[id="${id}"]`);
              const relItems = relSection ? [...relSection.querySelectorAll('.resource-list__item')] : [];

              // Filter out duplicates
              const filteredRelItems = relItems.filter(el => {
                const pid = el.querySelector('[data-product-id]')?.dataset.productId;
                return pid && !compIds.includes(pid);
              });

              // Build combined list
              const allItems = [...compItems, ...filteredRelItems].slice(0, maxProducts);

              if (allItems.length === 0) {
                this.#handleError(new Error('No recommendations available'));
                return;
              }

              // Use the related section as base (has correct wrapper HTML)
              const baseSection = compItems.length > 0 ? compSection : relSection;
              if (!baseSection) {
                this.#handleError(new Error('No recommendations available'));
                return;
              }

              // Replace items in the resource list
              const resourceList = baseSection.querySelector('.resource-list');
              if (resourceList) {
                resourceList.innerHTML = '';
                allItems.forEach(item => resourceList.appendChild(item));
              }

              this.dataset.recommendationsPerformed = 'true';
              this.innerHTML = baseSection.innerHTML;
            });
        })
        .catch((e) => this.#handleError(e));
    } else {
      // Related only - original logic
      this.#fetchCachedRecommendations(productId, sectionId, intent)
        .then((result) => {
          if (!result.success) {
            if (!Shopify.designMode) this.#handleError(new Error(`Server returned ${result.status}`));
            return;
          }
          const html = document.createElement('div');
          html.innerHTML = result.data || '';
          const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);
          if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
            this.dataset.recommendationsPerformed = 'true';
            this.innerHTML = recommendations.innerHTML;
          } else {
            this.#handleError(new Error('No recommendations available'));
          }
        })
        .catch((e) => this.#handleError(e));
    }
  }

  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;
    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) return { success: true, data: cachedResponse };

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) return { success: false, status: response.status };
      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  #handleError(error) {
    console.error('Product recommendations error:', error);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}